import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  Briefcase,
  Search as SearchIcon,
  ShieldCheck,
  Upload,
  User as UserIcon,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Spinner } from '@/components/ui/Spinner';
import { FileTypeIcon } from '@/components/files/FileTypeIcon';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { useOrganizations } from '@/api/organizations';
import { useSearchFiles } from '@/api/files';

type Section = 'Actions' | 'Organizations' | 'Files';

interface PaletteItem {
  /** Stable key, unique across the entire flat list. */
  key: string;
  section: Section;
  label: string;
  /** Optional secondary text rendered to the right (folder name, etc). */
  hint?: string;
  /** Renders the leading icon. Sized 16. */
  renderIcon: () => ReactNode;
  /** Lowercase string used by the simple substring matcher. */
  searchText: string;
  onSelect: () => void;
}

interface QuickAction {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** When true, only sysadmin / manager see it. */
  staffOnly?: boolean;
  /** When true, only client_admin / client_user see it. */
  clientOnly?: boolean;
  run: (ctx: { navigate: ReturnType<typeof useNavigate>; openUpload: () => void; organizationId?: string | null }) => void;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'upload',
    label: 'Upload files',
    Icon: Upload,
    run: ({ openUpload }) => openUpload(),
  },
  {
    id: 'search',
    label: 'Search files',
    Icon: SearchIcon,
    run: ({ navigate }) => navigate('/search'),
  },
  {
    id: 'profile',
    label: 'Profile',
    Icon: UserIcon,
    run: ({ navigate }) => navigate('/profile'),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    Icon: Bell,
    run: ({ navigate }) => navigate('/notifications'),
  },
  {
    id: 'my-workspace',
    label: 'My Workspace',
    Icon: Briefcase,
    clientOnly: true,
    run: ({ navigate, organizationId }) => {
      if (organizationId) navigate(`/workspace/${organizationId}`);
    },
  },
  {
    id: 'onboard',
    label: 'Onboard new client',
    Icon: UserPlus,
    staffOnly: true,
    run: ({ navigate }) => navigate('/admin/onboard'),
  },
  {
    id: 'audit',
    label: 'View audit log',
    Icon: ShieldCheck,
    staffOnly: true,
    run: ({ navigate }) => navigate('/admin/audit'),
  },
];

const SECTION_ORDER: Section[] = ['Actions', 'Organizations', 'Files'];

/**
 * Cmd+K / Ctrl+K command palette.
 *
 * Lightweight overlay (no Modal chrome). Opened from anywhere via the
 * keyboard shortcut, the TopBar hint button, or programmatically through
 * the UI store.
 *
 * Search:
 *  - Quick actions and orgs are filtered locally (case-insensitive substring).
 *  - Files only fire the API once the query is at least 2 chars, debounced
 *    300 ms via a deferred state.
 */
export function CommandPalette() {
  const open = useUIStore((s) => s.commandPaletteOpen);
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const setQuickUploadOpen = useUIStore((s) => s.setQuickUploadOpen);
  const { user } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Global keydown — Ctrl+K opens, Escape closes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K') && !e.altKey) {
        e.preventDefault();
        useUIStore.getState().setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape' && useUIStore.getState().commandPaletteOpen) {
        e.preventDefault();
        useUIStore.getState().setCommandPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Reset state every time the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setHighlight(0);
    // Defer focus to ensure the input has actually mounted.
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Debounce the query (300 ms) for the file search API call.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  const isStaff = user?.role === 'sysadmin' || user?.role === 'manager';
  const isClient = user?.role === 'client_admin' || user?.role === 'client_user';

  const orgs = useOrganizations();
  const filesQuery = useSearchFiles({
    q: debouncedQuery.trim().length >= 2 ? debouncedQuery.trim() : undefined,
    limit: 8,
  });

  const close = useCallback(() => setOpen(false), [setOpen]);

  // --- Build the flat result list -----------------------------------------

  const actionItems = useMemo<PaletteItem[]>(() => {
    return QUICK_ACTIONS
      .filter((a) => {
        if (a.staffOnly && !isStaff) return false;
        if (a.clientOnly && !isClient) return false;
        return true;
      })
      .map((a) => ({
        key: `action:${a.id}`,
        section: 'Actions' as const,
        label: a.label,
        renderIcon: () => <a.Icon size={16} className="text-bb-navy" />,
        searchText: a.label.toLowerCase(),
        onSelect: () => {
          close();
          a.run({
            navigate,
            openUpload: () => setQuickUploadOpen(true),
            organizationId: activeOrgId ?? user?.organization_id,
          });
        },
      }));
  }, [isStaff, isClient, navigate, close, setQuickUploadOpen, activeOrgId, user?.organization_id]);

  const orgItems = useMemo<PaletteItem[]>(() => {
    // Client users don't need cross-org navigation
    if (isClient) return [];
    const list = orgs.data ?? [];
    return list.map((o) => ({
      key: `org:${o.id}`,
      section: 'Organizations' as const,
      label: `Org: ${o.name}`,
      renderIcon: () => <Building2 size={16} className="text-bb-navy" />,
      searchText: o.name.toLowerCase(),
      onSelect: () => {
        close();
        navigate(`/workspace/${o.id}`);
      },
    }));
  }, [isClient, orgs.data, navigate, close]);

  const fileItems = useMemo<PaletteItem[]>(() => {
    const list = (filesQuery.data?.items ?? []).filter((f) => !f.is_deleted);
    return list.map((f) => ({
      key: `file:${f.id}`,
      section: 'Files' as const,
      label: `File: ${f.name}`,
      hint: f.folder_name ?? undefined,
      renderIcon: () => <FileTypeIcon name={f.name} size={16} />,
      searchText: `${f.name} ${f.folder_name ?? ''}`.toLowerCase(),
      onSelect: () => {
        close();
        navigate(`/files/${f.id}`);
      },
    }));
  }, [filesQuery.data, navigate, close]);

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    // When empty: show all actions + orgs (the "Recent" view per spec).
    if (!q) {
      return [...actionItems, ...orgItems];
    }
    const matches = (it: PaletteItem) => it.searchText.includes(q);
    // Files come back already matched from the server; don't re-filter them
    // (the server may match content beyond just the name).
    return [
      ...actionItems.filter(matches),
      ...orgItems.filter(matches),
      ...fileItems,
    ];
  }, [q, actionItems, orgItems, fileItems]);

  // Group for rendering. Preserves SECTION_ORDER.
  const grouped = useMemo(() => {
    const map = new Map<Section, PaletteItem[]>();
    for (const sec of SECTION_ORDER) map.set(sec, []);
    for (const it of filtered) map.get(it.section)!.push(it);
    return SECTION_ORDER.filter((s) => (map.get(s) ?? []).length > 0).map(
      (s) => ({ section: s, items: map.get(s)! }),
    );
  }, [filtered]);

  // Reset / clamp the highlighted index whenever results change.
  useEffect(() => {
    setHighlight((h) => {
      if (filtered.length === 0) return 0;
      if (h >= filtered.length) return filtered.length - 1;
      return h;
    });
  }, [filtered.length]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-palette-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setHighlight((h) => (h + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[highlight];
      if (item) item.onSelect();
    }
    // Escape is handled by the global listener.
  };

  if (!open) return null;

  const showFilesLoading =
    debouncedQuery.trim().length >= 2 && filesQuery.isFetching;

  // Compute the absolute (flat) index per item so the highlight maps correctly
  // across grouped rendering.
  let cursor = -1;

  const placeholder = isClient
    ? 'Search files, actions…'
    : 'Search organizations, files, actions…';

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={close}
    >
      <div
        className="mx-auto mt-20 w-full max-w-xl bg-white border border-bb-lilac/60 shadow-lg flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
          <SearchIcon size={18} className="text-bb-navy/70 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            aria-label="Search"
            aria-controls="command-palette-list"
            aria-activedescendant={
              filtered[highlight] ? `cmdk-opt-${filtered[highlight].key}` : undefined
            }
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-slate-500 border border-slate-300 bg-slate-50">
            Esc
          </kbd>
        </div>

        <div
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          aria-label="Results"
          className="flex-1 overflow-y-auto custom-scrollbar"
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {orgs.isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size={14} /> Loading…
                </span>
              ) : showFilesLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size={14} /> Searching files…
                </span>
              ) : (
                'No results'
              )}
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.section} className="py-1">
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.section}
                </div>
                {group.items.map((item) => {
                  cursor += 1;
                  const idx = cursor;
                  const isActive = idx === highlight;
                  return (
                    <button
                      key={item.key}
                      id={`cmdk-opt-${item.key}`}
                      data-palette-index={idx}
                      role="option"
                      aria-selected={isActive}
                      type="button"
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => item.onSelect()}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-left text-sm',
                        isActive ? 'bg-bb-navy/10 text-bb-navy' : 'text-slate-700',
                      )}
                    >
                      <span className="shrink-0 inline-flex items-center justify-center w-5 h-5">
                        {item.renderIcon()}
                      </span>
                      <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      {item.hint ? (
                        <span className="text-xs text-slate-500 truncate max-w-[40%]">
                          {item.hint}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
          {showFilesLoading && filtered.length > 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 border-t border-slate-100">
              <Spinner size={12} /> Searching files…
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-[11px] text-slate-500">
          <span>{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>
          <span className="hidden sm:inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 font-mono border border-slate-300 bg-slate-50">
                ↑
              </kbd>
              <kbd className="px-1.5 py-0.5 font-mono border border-slate-300 bg-slate-50">
                ↓
              </kbd>
              navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 font-mono border border-slate-300 bg-slate-50">
                ↵
              </kbd>
              select
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
